<template>
  <div class="sql-editor-container">
    <div v-show="loading" class="skeleton-placeholder" :style="{ height: height + 'px' }"></div>
    <div ref="editorContainer" class="sql-editor" :style="{ height: height + 'px' }" v-show="!loading"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import * as monaco from 'monaco-editor'
import { setupMonaco } from '../lib/monaco-setup'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  height: {
    type: Number,
    default: 300
  },
  language: {
    type: String,
    default: 'sql'
  },
  readOnly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const editorContainer = ref(null)
const loading = ref(true)
let editor = null

// 初始化 Monaco Editor
const initEditor = async () => {
  try {
    loading.value = true
    console.log('Start editor initialization, loading:', loading.value)

    // 确保元素已挂载
    await nextTick()
    if (!editorContainer.value) {
      throw new Error('Editor container element not found')
    }

    console.log('Editor container found, proceeding with initialization')

    if (!editorContainer.value) {
      console.error('Editor container not found after retries')
      throw new Error('Editor container element not found')
    }

    console.log('Setting up Monaco configuration')
    setupMonaco()

    console.log('Creating Monaco editor instance')
    editor = monaco.editor.create(editorContainer.value, {
      value: props.modelValue,
      language: props.language,
      theme: 'vs-light',
      fontSize: 14,
      lineHeight: 1.5,
      fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      readOnly: props.readOnly,
      wordWrap: 'on',
      lineNumbers: 'on',
      folding: true,
      showFoldingControls: 'mouseover',
      matchBrackets: 'always',
      autoIndent: 'full',
      formatOnPaste: true,
      formatOnType: true,
      suggestOnTriggerCharacters: true,
      quickSuggestions: true,
      parameterHints: { enabled: true },
      renderLineHighlight: 'all',
      renderWhitespace: 'boundary',
      tabSize: 2,
      insertSpaces: true,
      detectIndentation: true,
      trimAutoWhitespace: true,
      bracketPairColorization: { enabled: true },
      guides: {
        indentation: true,
        bracketPairs: true
      },
      padding: { top: 8, bottom: 8 },
      lineDecorationsWidth: 10,
      scrollbar: {
        verticalScrollbarSize: 8,
        horizontalScrollbarSize: 8,
        useShadows: false
      },
      overviewRulerLanes: 0,
      hideCursorInOverviewRuler: true,
      renderLineHighlightOnlyWhenFocus: true,
      fixedOverflowWidgets: true
    })

    // 监听内容变化
    editor.onDidChangeModelContent(() => {
      const value = editor.getValue()
      emit('update:modelValue', value)
      emit('change', value)
    })

    loading.value = false
    console.log('Editor initialized successfully')

  } catch (error) {
    console.error('Failed to initialize Monaco Editor:', error)
    loading.value = false
    console.log('Editor initialization failed, final loading state:', loading.value)
  }
}


// 格式化 SQL
const formatSql = () => {
  if (editor) {
    editor.getAction('editor.action.formatDocument').run()
  }
}

// 监听 modelValue 变化
watch(() => props.modelValue, (newValue) => {
  if (editor && editor.getValue() !== newValue) {
    editor.setValue(newValue)
  }
})

// 监听高度变化
watch(() => props.height, () => {
  nextTick(() => {
    if (editor) {
      editor.layout()
    }
  })
})

onMounted(() => {
  initEditor()
})

onUnmounted(() => {
  if (editor) {
    editor.dispose()
  }
})
// 暴露方法给父组件
defineExpose({
  formatSql,
  getEditor: () => editor
})
</script>

<style scoped>
.sql-editor-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.sql-editor {
  width: 100%;
}

.skeleton-placeholder {
  width: 100%;
  background: #f5f7fa;
}

/* 确保编辑器内容从左侧开始对齐 */
.sql-editor :deep(.monaco-editor) {
  text-align: left !important;
}

.sql-editor :deep(.view-lines) {
  text-align: left !important;
}

.sql-editor :deep(.view-line) {
  text-align: left !important;
}

.sql-editor :deep(.mtk1) {
  text-align: left !important;
}
</style>
